import {
  keepPreviousData,
  useMutation,
  useQuery,
} from '@tanstack/solid-query';
import { queryClient } from '@queries/client';
import { whatsappGraphql } from './client';
import { whatsappKeys } from './keys';

export interface WhatsappAccount {
  accountId: string;
  phoneE164: string;
  jid: string;
  displayName: string;
  /** CONNECTED | DISCONNECTED | PENDING | LINKED | LOGGED_OUT | UNSPECIFIED */
  state: string;
  /** WEB | MOBILE */
  transport: string;
  updatedAtUnixMs: string;
}

export type WhatsappLoginMethod = 'QR' | 'PAIRING' | 'SMS' | 'VOICE' | 'WHATSAPP';

export interface WhatsappLoginStarted {
  accountId: string;
  qrPayload: string | null;
  pairingCode: string | null;
  codeRequested: boolean;
  linked: boolean;
  error: string | null;
}

const ACCOUNTS_QUERY = `query WhatsappAccounts {
  user {
    whatsappAccounts {
      accountId
      phoneE164
      jid
      displayName
      state
      transport
      updatedAtUnixMs
    }
  }
}`;

export async function fetchWhatsappAccounts(): Promise<WhatsappAccount[]> {
  const data = await whatsappGraphql<{
    user: { whatsappAccounts: WhatsappAccount[] };
  }>(ACCOUNTS_QUERY);
  return data.user.whatsappAccounts;
}

/** The viewer's linked WhatsApp accounts. Polls so freshly-linked and
 * (dis)connected accounts surface without a manual refresh. */
export function useWhatsappAccountsQuery(refetchInterval = 20000) {
  return useQuery(() => ({
    queryKey: whatsappKeys.accounts,
    queryFn: () => fetchWhatsappAccounts(),
    // Hold the last account list during background refetches so the sidebar /
    // switcher never flashes empty. Ride out transient gateway blips, then
    // slow-poll (not stop) on a settled error so accounts self-recover.
    placeholderData: keepPreviousData,
    retry: 2,
    refetchInterval: (query) =>
      query.state.status === 'error' ? 30000 : refetchInterval,
  }));
}

export function invalidateWhatsappAccounts() {
  return queryClient.invalidateQueries({ queryKey: whatsappKeys.accounts });
}

const START_LOGIN = `mutation WhatsappStartLogin($input: WhatsappStartLoginInput!) {
  whatsappStartLogin(input: $input) {
    accountId
    qrPayload
    pairingCode
    codeRequested
    linked
    error
  }
}`;

export function useStartLoginMutation() {
  return useMutation(() => ({
    gcTime: 0,
    mutationFn: async (input: {
      method: WhatsappLoginMethod;
      displayName?: string;
      phoneE164?: string;
    }) => {
      const data = await whatsappGraphql<{
        whatsappStartLogin: WhatsappLoginStarted;
      }>(START_LOGIN, { input });
      return data.whatsappStartLogin;
    },
    onSettled: () => invalidateWhatsappAccounts(),
  }));
}

const SUBMIT_CODE = `mutation WhatsappSubmitCode($accountId: String!, $code: String!) {
  whatsappSubmitCode(accountId: $accountId, code: $code)
}`;

export function useSubmitCodeMutation() {
  return useMutation(() => ({
    gcTime: 0,
    mutationFn: async (vars: { accountId: string; code: string }) => {
      const data = await whatsappGraphql<{ whatsappSubmitCode: boolean }>(
        SUBMIT_CODE,
        vars
      );
      return data.whatsappSubmitCode;
    },
    onSettled: () => invalidateWhatsappAccounts(),
  }));
}

const LOGOUT = `mutation WhatsappLogout($accountId: String!) {
  whatsappLogout(accountId: $accountId)
}`;

export function useLogoutMutation() {
  return useMutation(() => ({
    gcTime: 0,
    mutationFn: async (vars: { accountId: string }) => {
      const data = await whatsappGraphql<{ whatsappLogout: boolean }>(
        LOGOUT,
        vars
      );
      return data.whatsappLogout;
    },
    onSettled: () => invalidateWhatsappAccounts(),
  }));
}
